'use client';

import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { config } from '@shared/config';

type DatadogSite = NonNullable<Parameters<typeof datadogRum.init>[0]['site']>;

type IgnoredErrorRule = {
    messageContains: string;
    sampleRate?: number;
};

type DatadogConfigExtra = {
    allowedTracingUrls?: Parameters<typeof datadogRum.init>[0]['allowedTracingUrls'];
    ignoredErrors?: IgnoredErrorRule[];
};

const CLIENT_TOKEN = process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN ?? '';
const APP_ID = process.env.NEXT_PUBLIC_DD_APP_ID ?? '';
const SERVICE = process.env.NEXT_PUBLIC_DD_SERVICE ?? '';
const VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? 'n/a';
const ENV = process.env.NEXT_PUBLIC_ENVIRONMENT ?? 'prod';

/** Parse a 0–100 sample rate from env; invalid/missing values fall back to the default. */
export function parseSampleRate(value: string | undefined, fallback: number): number {
    if (value === undefined || value === '') {
        return fallback;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return fallback;
    }
    return Math.min(100, Math.max(0, parsed));
}

const SESSION_SAMPLE_RATE = parseSampleRate(process.env.NEXT_PUBLIC_DD_SESSION_SAMPLE_RATE, 100);
const SESSION_REPLAY_SAMPLE_RATE = parseSampleRate(
    process.env.NEXT_PUBLIC_DD_SESSION_REPLAY_SAMPLE_RATE,
    2,
);
const TRACE_SAMPLE_RATE = parseSampleRate(process.env.NEXT_PUBLIC_DD_TRACE_SAMPLE_RATE, 10);

const { site, excludedActivityLinks, ignoredErrors } = (config.datadog ?? {}) as {
    site?: DatadogSite;
    excludedActivityLinks?: (string | RegExp)[];
    ignoredErrors?: IgnoredErrorRule[];
};

const SITE = site as DatadogSite | undefined;
const extraConfig = (config.datadog ?? {}) as DatadogConfigExtra;
const allowedTracingUrls = extraConfig.allowedTracingUrls;

const botPattern =
    '(googlebot\\/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';

const regex = typeof navigator !== 'undefined' ? new RegExp(botPattern, 'i') : null;
const conditionalSampleRate =
    typeof navigator !== 'undefined' && regex?.test(navigator.userAgent ?? '')
        ? 0
        : SESSION_SAMPLE_RATE;

const EXTENSION_NOISE_HOSTS = [
    'selnor.fun',
    'bzrcdn.openai.com',
    'microad.jp',
    // Browser-native translators (Chrome/Edge) that fire from the user's device
    // after the page loads, not from our code.
    'translate-pa.googleapis.com',
    'edge.microsoft.com',
    // Coupon / shopping / security extensions injected into the page.
    'coupertsh.com',
    'joinkudos.com',
    'cartera.com',
    'threatspike.com',
    'zijieapi.com',
];

const EXTENSION_NOISE_URLS = [
    // Google Translate feature endpoints that live on hosts we otherwise allow.
    'translate.googleapis.com/element/log',
    'translate.google.com/gen204',
    'fonts.gstatic.com/s/i/productlogos/translate/',
];

function isExtensionNoiseUrl(url: string): boolean {
    let host: string;
    try {
        const parsed = new URL(url);
        host = parsed.host;
    } catch {
        return EXTENSION_NOISE_URLS.some((pattern) => url.includes(pattern));
    }
    if (EXTENSION_NOISE_HOSTS.some((noise) => host === noise || host.endsWith(`.${noise}`))) {
        return true;
    }
    return EXTENSION_NOISE_URLS.some((pattern) => url.includes(pattern));
}

function shouldDiscardByIgnoredErrorRules(message?: string): boolean {
    if (!message) {
        return false;
    }

    const rules: IgnoredErrorRule[] = ignoredErrors ?? [];
    const rule = rules.find(({ messageContains }) => message.includes(messageContains));
    if (!rule) {
        return false;
    }

    const sampleRate = typeof rule.sampleRate === 'number' ? rule.sampleRate : 1;
    return Math.random() > sampleRate;
}

function beforeSend(event: RumEvent): boolean {
    if (event.view?.url) {
        event.view.url = event.view.url.replace(/^chrome-extension:\/\/[a-z]{32}\//i, '');
    }

    if (
        event.type === 'resource' &&
        event.resource?.url &&
        isExtensionNoiseUrl(event.resource.url)
    ) {
        return false;
    }

    if (event.error && event.type === 'error') {
        if (event.error.resource?.url && isExtensionNoiseUrl(event.error.resource.url)) {
            return false;
        }

        if (shouldDiscardByIgnoredErrorRules(event.error.message)) {
            return false;
        }

        if (event.error.stack) {
            Object.defineProperty(event.error, 'stack', {
                value: event.error.stack.replace(/chrome-extension:\/\/[a-z]{32}/gi, ''),
                writable: false,
                configurable: true,
            });
        }
    }

    return true;
}

// Do not run in SSR
if (typeof window !== 'undefined' && CLIENT_TOKEN && APP_ID) {
    datadogRum.init({
        applicationId: APP_ID,
        clientToken: CLIENT_TOKEN,
        site: SITE,
        service: SERVICE,
        env: ENV,
        version: VERSION,
        sessionSampleRate: conditionalSampleRate,
        sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        excludedActivityUrls: excludedActivityLinks,
        allowedTracingUrls: allowedTracingUrls?.length
            ? allowedTracingUrls
            : [{ match: /\/api\//, propagatorTypes: ['tracecontext', 'datadog'] }],
        traceContextInjection: 'sampled',
        traceSampleRate: TRACE_SAMPLE_RATE,
        silentMultipleInit: false,
        trackUserInteractions: true,
        beforeSend,
    });

    datadogLogs.init({
        clientToken: CLIENT_TOKEN,
        site: SITE,
        service: SERVICE,
        env: ENV,
        version: VERSION,
        forwardErrorsToLogs: true,
        sessionSampleRate: conditionalSampleRate,
        beforeSend: (log) => !shouldDiscardByIgnoredErrorRules(log.message),
    });

    datadogRum.startSessionReplayRecording();
}

export default function DatadogInitializer() {
    return null;
}
